import { useStore } from '@nanostores/react'
import type { Dayjs } from 'dayjs'
import React, { Fragment, useEffect, useState } from 'react'

import { Dialog } from '@/components/Dialog'
import { AdditionalInfoDialog } from '@/components/ProgramPanel/AdditionalInfoDialog'
import { PurchaseTypes } from '@/constants/programs'
import {
  useFlexibleProgram,
  type SelectedOptionsState,
} from '@/hooks/useFlexibleProgram'
import type { Delivery, DeliveryZone } from '@/models/delivery-zone'
import { sendInitialProductView } from '@/scripts/tracking'
import {
  transformActiveVariantToGTMItem,
  transformToChordItem,
} from '@/scripts/tracking/helper'
import { chord } from '@/scripts/tracking/chord'
import { $cart } from '@/stores/cart/stores'
import { closeDialog, openDialog } from '@/stores/dialog/actions'
import { $openedDialog } from '@/stores/dialog/computed'
import type {
  CMSAddonsOptionGroup,
  CMSProgramAddonOption,
  CMSProgramMealScheduleOption,
  CMSProgramTab,
} from '@/types/sanity'
import { getCartZipCode } from '@/utilities/commerce'
import { addProgramToCart } from '@/utilities/flexible-program'

import { Actions } from './Actions'
import { Addons } from './Addons'
import { DeliveryWindow } from './DeliveryWindow'
import { Footnote } from './Footnote'
import { MealScheduleOptions } from './MealScheduleOptions'
import { Group } from './ProgramOptions/Group'
import { Item } from './ProgramOptions/Item'
import { RedemptionOptions } from './RedemptionOptions'
import { RichText } from './RichText'

export type GroupedAddonsWithQuanity = {
  [groupId: string]: {
    addons: {
      addon: CMSProgramAddonOption
      quantity: number
    }[]
    group: CMSAddonsOptionGroup
  }
}

export type DeliveryDataState = {
  zipCode?: string
  startDate?: Dayjs
  deliveryZone?: DeliveryZone
  deliveries: Delivery[]
  downgradedSelectedOptions?: SelectedOptionsState
}

type Props = {
  tab: CMSProgramTab
}

export const FlexibleProgramForm: React.FC<Props> = ({ tab }) => {
  const [groupedAddonsWithQuantity, setGroupedAddonsWithQuantity] =
    useState<GroupedAddonsWithQuanity>({})
  const [deliveryData, setDeliveryData] = useState<DeliveryDataState>()
  const [amountToRedeem, setAmountToRedeem] = useState(0)
  const [isTracked, setIsTracked] = useState(false)
  const [schedulePowWeek, setSchedulePowWeek] = useState<boolean>(true)

  const cart = useStore($cart)
  const openedDialog = useStore($openedDialog)

  const { selectedOptions, dynamicOptionGroups, handleSelectedOptionChange } =
    useFlexibleProgram(tab)

  const effectiveSelectedOptions =
    deliveryData?.downgradedSelectedOptions || selectedOptions

  const isInfoModalEnabled = tab.infoModal?.isEnabled || false

  useEffect(() => {
    if (!selectedOptions.activeVariant) {
      return
    }

    setTimeout(() => {
      sendInitialProductView({
        data: transformActiveVariantToGTMItem(selectedOptions.activeVariant),
      })
    })
  }, [selectedOptions.activeVariant])

  useEffect(() => {
    const activeVariant = selectedOptions.activeVariant
    if (!activeVariant) {
      return
    }

    setTimeout(() => {
      const payload = {
        cart: {},
        product: transformToChordItem(
          activeVariant,
          null,
          groupedAddonsWithQuantity,
          selectedOptions.meals,
        ),
      }

      if (isTracked) {
        chord?.trackProductClicked(payload)
      } else {
        chord?.trackProductViewed(payload)
        setIsTracked(true)
      }
    })
  }, [selectedOptions.activeVariant, groupedAddonsWithQuantity])

  const handleDeliveryDataChange = (data: DeliveryDataState) => {
    setDeliveryData(data)
  }

  const handleSelectedAddonsChange = ({
    group,
    addonsWithQuantity,
  }: {
    group: CMSAddonsOptionGroup
    addonsWithQuantity: { addon: CMSProgramAddonOption; quantity: number }[]
  }) => {
    setGroupedAddonsWithQuantity((prev) => ({
      ...prev,
      [group._id]: {
        addons: addonsWithQuantity.map((addon) => ({
          addon: addon.addon,
          quantity: addon.quantity,
        })),
        group: group,
      },
    }))
  }

  const handleRedemptionOptionsChange = (amountToRedeem: number) => {
    setAmountToRedeem(amountToRedeem)
  }

  const handleMealScheduleOptionChange = (
    option: CMSProgramMealScheduleOption,
  ) => {
    setSchedulePowWeek(option.value === 'yes')
  }

  const handleAtcClick = async () => {
    if (isDeliveryWindowApplicable && deliveryData) {
      const cartZipCode = cart && getCartZipCode(cart)

      if (cartZipCode && cartZipCode !== deliveryData.zipCode) {
        openDialog('dual-zip-code-message')
        return
      }
    }

    await addProgramToCart({
      selectedOptions: effectiveSelectedOptions,
      groupedAddonsWithQuantity,
      deliveryData,
      schedulePowWeek,
      amountToRedeem,
      sectionId: tab._id,
      sectionType: tab._type,
      selectedOptionsBeforeDowngrade: deliveryData?.downgradedSelectedOptions
        ? selectedOptions
        : undefined,
      isInfoModalEnabled,
    })
  }

  const isDeliveryWindowApplicable =
    effectiveSelectedOptions.purchaseType === PurchaseTypes.SUBSCRIPTION ||
    (effectiveSelectedOptions.purchaseType === PurchaseTypes.PACK_OF_WEEKS &&
      schedulePowWeek)
  const isAtcActive =
    !!effectiveSelectedOptions.activeVariant && isDeliveryWindowApplicable
      ? !!deliveryData?.zipCode && !!deliveryData.startDate
      : true

  const totalPrice =
    (effectiveSelectedOptions.activeVariant?.price || 0) +
    Object.values(groupedAddonsWithQuantity).reduce(
      (acc, { addons }) =>
        acc +
        addons.reduce(
          (acc, { addon, quantity }) => acc + quantity * addon.variant.price,
          0,
        ),
      0,
    ) -
    amountToRedeem

  return (
    <div className="flexible-program-form">
      {tab.sections?.map((section) => (
        <Fragment key={section._id}>
          {section._type === 'web.program.programOptions' &&
            dynamicOptionGroups.map((group) => (
              <Group
                key={group.optionsType}
                label={group.label}
                isHidden={group.isHidden}
              >
                {group.options.map((groupOption) => (
                  <Item
                    key={groupOption.option._id}
                    {...groupOption}
                    selectedOptions={selectedOptions}
                    onChange={handleSelectedOptionChange}
                  />
                ))}
              </Group>
            ))}
          {section._type === 'program.deliveryOption' &&
            isDeliveryWindowApplicable && (
              <DeliveryWindow
                cmsConfig={section}
                selectedOptions={selectedOptions}
                onDeliveryDataChange={handleDeliveryDataChange}
              />
            )}
          {section._type === 'program.optionGroup' &&
            section.groupChildType === 'program.addonOption' && (
              <Addons
                content={section as CMSAddonsOptionGroup}
                onSelectedAddonsChange={handleSelectedAddonsChange}
              />
            )}
          {section._type === 'program.optionGroup' &&
            section.groupChildType === 'program.redemptionOption' && (
              <RedemptionOptions
                config={section}
                onChange={handleRedemptionOptionsChange}
              />
            )}
          {section._type === 'web.program.richTextSection' && (
            <RichText key={section._id} content={section.content} />
          )}
          {section._type === 'web.program.mealScheduleOptionGroup' && (
            <MealScheduleOptions
              config={section}
              onChange={handleMealScheduleOptionChange}
            />
          )}
        </Fragment>
      ))}
      <div className="flexible-program-form__footer">
        <Actions
          isActive={isAtcActive}
          price={totalPrice}
          purchaseType={selectedOptions.purchaseType}
          onAtcClick={handleAtcClick}
        />
        {tab.footnote && <Footnote text={tab.footnote} />}
      </div>
      <Dialog
        isOpen={openedDialog?.id === 'dual-zip-code-message'}
        onClose={closeDialog}
      >
        <div className="dual-zip-code-message-dialog">
          <p>
            Hi Sakaralite! Currently we can only process orders that have the
            same delivery zip code. If you would like to place an order for two
            programs being sent to different addresses, they must be placed as
            separate orders.
          </p>
          <button className="button button" onClick={closeDialog}>
            Got it
          </button>
        </div>
      </Dialog>
      {isInfoModalEnabled && <AdditionalInfoDialog {...tab.infoModal} />}
    </div>
  )
}
